import { useState, useEffect, useRef, useCallback } from "react";
import { getWeighbridgeConfig, closeSerialPort, weighbridgeSerialReadUrl } from "../pages/generic/crud";

const CAPTURE_TIMEOUT_MS = 60000;   // overall give-up if no valid data ever arrives

// Stabilization: the port is opened mid-stream, so the first reading can be a
// partial-line fragment, and the scale bounces while the load settles. Instead
// of taking the first valid number, we wait for readings to agree within a
// tolerance band for a short settle window before accepting.
//
// Tolerance is configurable per organization on the Weighbridge Setup page,
// entered in kg (default 25). Parsers return tons, so we divide by 1000 to
// compare in the same unit as the parsed reading (25 kg -> 0.025 t).
const DEFAULT_TOLERANCE_KG = 25;
const STABILIZE_SETTLE_MS = 1500;   // the agreeing cluster must span at least this long
const STABILIZE_MAX_MS = 7000;      // hard cap; at this point use the best value seen
const STABILIZE_MIN_READINGS = 2;   // need at least this many agreeing readings

// Live snapshot of an in-progress capture, surfaced so the UI can explain to the
// user exactly what is happening (and why there is a short wait).
//   phase:     'connecting' | 'waiting' | 'stabilizing'
//   latest:    most recent valid parsed reading (live value the scale is showing)
//   readings:  size of the current "agreeing" cluster (how steady it is right now)
//   seen:      total valid readings received so far
//   startedAt: ms timestamp of the first reading (drives the settle progress bar)
const EMPTY_INFO = {
    phase: 'connecting',
    latest: null,
    readings: 0,
    seen: 0,
    startedAt: null,
    settleMs: STABILIZE_SETTLE_MS,
    maxMs: STABILIZE_MAX_MS,
};

/**
 * Hook for capturing weight from the weighbridge via SSE.
 * Connects to the serial-read SSE endpoint, waits for the first valid parsed weight,
 * then closes the connection and returns the result.
 *
 * @param {boolean} active - Whether the hook should load the parser (e.g., when dialog is open)
 * @returns {{ captureWeight, isCapturing, captureStatus, captureInfo }}
 */
export function useWeighbridgeCapture(active) {
    const [isCapturing, setIsCapturing] = useState(false);
    const [captureStatus, setCaptureStatus] = useState('');
    const [captureInfo, setCaptureInfo] = useState(EMPTY_INFO);
    const parserFnRef = useRef(null);
    const cleanupRef = useRef(null);
    // Tolerance in parsed units (tons). Set from config; falls back to the default.
    const toleranceRef = useRef(DEFAULT_TOLERANCE_KG / 1000);

    // Load parser function + stabilization tolerance from the SINGLETON weighbridge
    // config. This is only the LEGACY FALLBACK used when a capture runs without a
    // selected setup group; group captures pass their own weighbridge config to
    // captureWeight() and never touch these refs.
    useEffect(() => {
        if (active) {
            getWeighbridgeConfig().then(({ data }) => {
                if (data && data.parserFunction) {
                    try {
                        parserFnRef.current = new Function('data', data.parserFunction + '\nreturn parse(data);');
                    } catch (e) {
                        parserFnRef.current = null;
                    }
                }
                const kg = data && data.stabilizationToleranceKg > 0
                    ? data.stabilizationToleranceKg
                    : DEFAULT_TOLERANCE_KG;
                toleranceRef.current = kg / 1000;  // kg -> tons (parser unit)
            }).catch(() => {});
        }
    }, [active]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (cleanupRef.current) {
                cleanupRef.current();
            }
        };
    }, []);

    // captureWeight(options)
    //   options.weighbridge - the SELECTED setup group's embedded weighbridge config
    //                         ({ parserFunction, stabilizationToleranceKg, ... }); when
    //                         present it drives the parser + tolerance for this capture.
    //   options.groupId     - the selected group id, threaded into the SSE URL so the
    //                         backend reads from that group's serial port.
    //   With neither (legacy / single-device), we fall back to the singleton config
    //   loaded on `active` and the plain serial-read URL.
    const captureWeight = useCallback((options = {}) => {
        const { weighbridge = null, groupId = null } = options;
        return new Promise((resolve, reject) => {
            // Resolve the parser + tolerance for THIS capture. A selected group's
            // weighbridge wins; otherwise use the singleton fallback refs.
            let parserFn = parserFnRef.current;
            let tolerance = toleranceRef.current;
            if (weighbridge) {
                parserFn = null;
                if (weighbridge.parserFunction) {
                    try {
                        parserFn = new Function('data', weighbridge.parserFunction + '\nreturn parse(data);');
                    } catch (e) {
                        parserFn = null;
                    }
                }
                const kg = weighbridge.stabilizationToleranceKg > 0
                    ? weighbridge.stabilizationToleranceKg
                    : DEFAULT_TOLERANCE_KG;
                tolerance = kg / 1000;  // kg -> tons (parser unit)
            }

            setIsCapturing(true);
            setCaptureStatus('Connecting to weighbridge...');
            setCaptureInfo({ ...EMPTY_INFO, phase: 'connecting' });

            const es = new EventSource(weighbridgeSerialReadUrl(groupId));
            let settled = false;
            let stabilizeTimer = null;
            let seen = 0;          // total valid readings received
            let startedAt = null;  // first-reading timestamp

            const cleanup = () => {
                if (settled) return;
                settled = true;
                cleanupRef.current = null;
                clearTimeout(timeout);
                if (stabilizeTimer) clearTimeout(stabilizeTimer);
                es.close();
                closeSerialPort().catch(() => {});
                setIsCapturing(false);
                setCaptureStatus('');
                setCaptureInfo(EMPTY_INFO);
            };

            // Store cleanup ref so unmount can call it
            cleanupRef.current = cleanup;

            const timeout = setTimeout(() => {
                cleanup();
                reject(new Error("Timed out waiting for valid weight data"));
            }, CAPTURE_TIMEOUT_MS);

            es.addEventListener('status', (event) => {
                console.log('[Weighbridge] Status:', event.data);
                if (settled) return;
                setCaptureStatus('Waiting for valid weight data...');
                // Only advance to 'waiting' before any reading has arrived; don't
                // clobber 'stabilizing' once data is flowing.
                setCaptureInfo((prev) =>
                    prev.seen > 0 ? prev : { ...prev, phase: 'waiting' }
                );
            });

            // Test mode: backend sends a pre-parsed weight directly
            es.addEventListener('test-weight', (event) => {
                if (settled) return;
                const weight = parseFloat(event.data);
                console.log('[Weighbridge] Test weight received:', weight);
                if (!isNaN(weight) && weight > 0) {
                    cleanup();
                    resolve(weight);
                }
            });

            // Stabilization state: group agreeing readings into a cluster; a
            // partial/garbage fragment forms its own tiny cluster and is dropped.
            let cluster = null;       // { values: [], startT }
            let bestCluster = null;   // largest cluster seen so far (cap fallback)

            // Use the LAST reading of a settled cluster as the weight — averaging
            // (and rounding it) would throw away the decimal part (e.g. 15.000).
            const clusterLast = (c) => c.values[c.values.length - 1];

            const resolveBest = () => {
                if (settled) return;
                if (bestCluster) {
                    const w = clusterLast(bestCluster);
                    console.log('[Weighbridge] Settle cap reached, using last value:', w,
                        'from', bestCluster.values.length, 'readings');
                    cleanup();
                    resolve(w);
                } else {
                    cleanup();
                    reject(new Error("No stable weight reading"));
                }
            };

            es.addEventListener('data', (event) => {
                if (settled) return;
                console.log('[Weighbridge] Raw data:', event.data);
                if (!parserFn) return;

                let result;
                try {
                    result = parserFn(event.data);
                } catch (e) {
                    console.log('[Weighbridge] Parse error:', e.message);
                    return;
                }
                if (!(typeof result === 'number' && !isNaN(result) && result > 0)) return;

                const now = Date.now();
                // Arm the hard settle cap on the first valid reading.
                if (stabilizeTimer === null) {
                    stabilizeTimer = setTimeout(resolveBest, STABILIZE_MAX_MS);
                    startedAt = now;
                }
                seen += 1;

                // Add to the current cluster if it agrees with the previous
                // reading within tolerance; else start a fresh cluster (this is
                // how a partial fragment is shed).
                if (cluster && Math.abs(result - clusterLast(cluster)) <= tolerance) {
                    cluster.values.push(result);
                } else {
                    cluster = { values: [result], startT: now };
                }
                if (!bestCluster || cluster.values.length > bestCluster.values.length) {
                    bestCluster = cluster;
                }
                setCaptureStatus('Stabilizing weight... (' + cluster.values.length + ' readings)');
                setCaptureInfo({
                    phase: 'stabilizing',
                    latest: result,
                    readings: cluster.values.length,
                    seen,
                    startedAt,
                    settleMs: STABILIZE_SETTLE_MS,
                    maxMs: STABILIZE_MAX_MS,
                });

                // Accept once the agreeing cluster has held for the settle window.
                if (cluster.values.length >= STABILIZE_MIN_READINGS &&
                    (now - cluster.startT) >= STABILIZE_SETTLE_MS) {
                    const w = clusterLast(cluster);
                    console.log('[Weighbridge] Stable weight:', w, 'from', cluster.values.length, 'readings');
                    cleanup();
                    resolve(w);
                }
            });

            es.addEventListener('error', (event) => {
                if (settled) return;
                console.log('[Weighbridge] SSE error event:', event.data);
                if (event.data) {
                    cleanup();
                    reject(new Error(event.data));
                }
            });

            es.onerror = (event) => {
                if (settled) return;
                console.log('[Weighbridge] Connection error, closing');
                cleanup();
                reject(new Error("Connection to weighbridge lost"));
            };
        });
    }, []);

    return { captureWeight, isCapturing, captureStatus, captureInfo };
}
